import { ProjectRepresentative, UserProfile } from '../types';

/**
 * Verantwortliche Planungsphase bei der Projektanlage
 * (docs/projekt-kickoff-plan.md §2 und §5, Entscheid 12.10.).
 *
 * Der Assistent lässt jeden, der Verantwortliche zuweisen darf (Admin oder
 * Recht `projekte.zuweisen`), mehrere Personen wählen; leer heißt: der
 * Hauptverantwortliche plant selbst. `App.handleSetupComplete` hängt die
 * Gewählten mit `bereich: 'planung'` an die Vertreterliste, BEVOR das Projekt
 * gespeichert wird — der Kickoff-Trigger legt aus genau dieser Liste je
 * Person eine Kalender-Quittung an. Die Angebotsannahme
 * (`AcceptQuoteDialog` → `crmService.acceptQuote`) macht dasselbe mit
 * denselben Regeln.
 *
 * Reine Funktionen, ohne DOM und ohne Supabase.
 */

/** Anzeigename eines Profils — nie leer, solange es eine E-Mail gibt. */
export const profilName = (user: Pick<UserProfile, 'first_name' | 'last_name' | 'email'>): string =>
    [user.first_name, user.last_name].filter(Boolean).join(' ').trim() || user.email || '';

/**
 * Entfernt den Besitzer und doppelte Personen, die erste Nennung gewinnt.
 *
 * Warum der Besitzer raus muss: Dieselbe Person als Position 0 UND als
 * Planungseintrag ergäbe eine zweite Kickoff-Quittung für sie, und
 * `applyRepresentatives` würfe den Planungseintrag beim Speichern ohnehin
 * wieder weg — die Zuweisungsnotiz nennte dann eine Rolle, die es nicht gibt.
 */
const ohneBesitzerUndDubletten = (
    users: (UserProfile | null | undefined)[],
    besitzerId: string | undefined,
): UserProfile[] => {
    const gesehen = new Set<string>();
    const out: UserProfile[] = [];
    for (const user of users) {
        const id = (user?.id || '').trim();
        if (!user || !id) continue;
        if (besitzerId && id === besitzerId) continue;
        if (gesehen.has(id)) continue;
        gesehen.add(id);
        out.push(user);
    }
    return out;
};

/**
 * Gewählte ids → Profile, in Auswahlreihenfolge, ohne Besitzer und Dubletten.
 * Ids, die in `verfuegbar` nicht (mehr) vorkommen, fallen still weg.
 *
 * Gemeinsamer Kern für den Assistenten, den Annahmedialog (Chips und
 * Übergabe) und `crmService.acceptQuote` (dort mit den frisch geladenen
 * Profilen): Alle drei müssen dieselbe Liste ergeben, sonst zeigte der Dialog
 * jemanden, der im Projekt dann fehlt — oder umgekehrt.
 */
export const planungsPersonenAusIds = (
    ids: readonly string[] | null | undefined,
    verfuegbar: readonly UserProfile[],
    besitzerId: string | undefined,
): UserProfile[] =>
    ohneBesitzerUndDubletten((ids || []).map(id => verfuegbar.find(u => u.id === id)), besitzerId);

/**
 * Wer im Auswahlfeld „+ Person hinzufügen …" noch angeboten wird: alle außer
 * dem Hauptverantwortlichen und den schon Gewählten. Den Besitzer gar nicht
 * erst anzubieten ist ehrlicher als ihn nach dem Klick still zu verwerfen.
 */
export const waehlbarFuerPlanung = (
    verfuegbar: readonly UserProfile[],
    besitzerId: string | undefined,
    gewaehlteIds: readonly string[],
): UserProfile[] =>
    verfuegbar.filter(u => !!u.id && u.id !== besitzerId && !gewaehlteIds.includes(u.id));

/**
 * Auswahl im Assistenten → Profile für `onComplete`.
 *
 * - Nur wer Verantwortliche zuweisen darf (Admin oder Recht
 *   `projekte.zuweisen`, Rollensystem), sieht die Auswahl; bei allen anderen
 *   gilt sie nie, auch wenn noch ein Wert im Zustand stünde.
 * - SiFa kennt keine Planungsphase (Vorankündigung, SiGe-Plan …): nie setzen,
 *   auch wenn vor dem Typwechsel jemand gewählt war.
 * - Ids, die in der Kollegenliste nicht (mehr) vorkommen, fallen still weg.
 */
export const planungsPersonenAusAuswahl = (
    ids: string[],
    verfuegbar: UserProfile[],
    besitzerId: string | undefined,
    { darfZuweisen, isSifa }: { darfZuweisen: boolean; isSifa: boolean },
): UserProfile[] => {
    if (!darfZuweisen || isSifa) return [];
    return planungsPersonenAusIds(ids, verfuegbar, besitzerId);
};

/** Was der Assistent übergibt — alter und neuer Feldname. */
export interface PlanungsAngabe {
    planningUsers?: (UserProfile | null | undefined)[] | null;
    /** Alter Feldname (eine Person). Nur gelesen, wenn `planningUsers` fehlt. */
    planningUser?: UserProfile | null;
}

/**
 * Daten des Assistenten → Planungspersonen für die Anlage.
 *
 * Rückwärtskompatibel: Fehlt `planningUsers`, aber `planningUser` ist da (ein
 * noch offener Assistent aus einem älteren Build), zählt diese eine Person.
 */
export const planungsPersonenAusSetup = (
    data: PlanungsAngabe | null | undefined,
    besitzerId: string | undefined,
): UserProfile[] => {
    const liste = Array.isArray(data?.planningUsers)
        ? data!.planningUsers!
        : data?.planningUser ? [data.planningUser] : [];
    return ohneBesitzerUndDubletten(liste, besitzerId);
};

/** Planungspersonen als Einträge der Vertreterliste. */
export const planungsEintraege = (users: UserProfile[]): ProjectRepresentative[] =>
    users.map(user => ({
        id: user.id,
        name: profilName(user),
        email: user.email,
        phone: user.phone,
        bereich: 'planung' as const,
    }));

/** „A", „A und B", „A, B und C". */
export const nenneNamen = (namen: string[]): string => {
    const liste = namen.map(n => (n || '').trim()).filter(Boolean);
    if (liste.length <= 1) return liste[0] || '';
    return `${liste.slice(0, -1).join(', ')} und ${liste[liste.length - 1]}`;
};

/**
 * Text der Zuweisungsnotiz im Projektprotokoll. Mit einer Planungsperson
 * wortgleich zur bisherigen Notiz; bei mehreren „sowie", damit nicht zwei
 * „und" hintereinander stehen.
 */
export const zuweisungsNotizText = (besitzerName: string, planungsNamen: string[]): string => {
    const namen = planungsNamen.map(n => (n || '').trim()).filter(Boolean);
    if (namen.length === 0) return `Projekt wurde an ${besitzerName} zugewiesen.`;
    const bindung = namen.length > 1 ? 'sowie' : 'und';
    return `Projekt wurde an ${besitzerName} (Ausführung) ${bindung} ${nenneNamen(namen)} (Planung) zugewiesen.`;
};
